import { ref, onMounted, computed, watch, nextTick } from 'vue';
import Header from '@/components/HeaderDashboard.vue';
import Sidebar from '@/components/Sidebar.vue';
import TrasladosTab from './Traslados.vue';
import { AlmacenService } from '@/services/AlmacenService';
import { hasPermission } from '@/store/userStore';
import { PERMISSIONS } from '@/constants/permissions';
import Swal from 'sweetalert2';
import { PlusIcon, SearchIcon, HomeIcon, EditIcon, TrashIcon, XIcon, MapPinIcon, ArchiveIcon, SettingsIcon, FileTextIcon, ScaleIcon, HashIcon, BriefcaseIcon, CalendarIcon, FileUpIcon, CheckCircleIcon, TagIcon, LayersIcon, MaximizeIcon, GlobeIcon, MapIcon, PhoneIcon, MailIcon, Loader2Icon, PowerIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, NavigationIcon, TruckIcon, PackageIcon, BoxIcon } from 'lucide-vue-next';
// Utility for toasts like in Retail
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
});
export default {
    name: 'AlmacenesRetail',
    components: {
        Header, Sidebar, TrasladosTab, PlusIcon, SearchIcon, HomeIcon, EditIcon, TrashIcon, XIcon,
        MapPinIcon, ArchiveIcon, SettingsIcon, FileTextIcon, ScaleIcon,
        HashIcon, BriefcaseIcon, CalendarIcon, FileUpIcon, CheckCircleIcon,
        TagIcon, LayersIcon, MaximizeIcon, GlobeIcon, MapIcon, PhoneIcon,
        MailIcon, Loader2Icon, PowerIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, NavigationIcon, TruckIcon, PackageIcon, BoxIcon
    },
    setup() {
        const activeTab = ref('almacenes');
        const almacenes = ref([]);
        const tiposAlmacen = ref([]);
        const isLoading = ref(false);
        // Filters and Pagination
        const searchQuery = ref('');
        const filterTipoId = ref(0);
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        const selectedLetter = ref("");
        const currentPage = ref(1);
        const itemsPerPage = 8;
        const totalCount = ref(0);
        const isModalOpen = ref(false);
        const fileInput = ref(null);
        const isEditing = ref(false);
        const isSaving = ref(false);
        const selectedFile = ref(null);
        // Inventory State
        const isInventoryModalOpen = ref(false);
        const isInventoryLoading = ref(false);
        const inventoryItems = ref([]);
        const selectedAlmacen = ref(null);
        const searchInventoryQuery = ref('');
        const form = ref({
            almacenId: 0,
            tipoAlmacenId: '',
            numeroExpediente: '',
            numeroTomo: '',
            nombreRegistroMercantil: '',
            numeroRegistro: '',
            numeroAlmacen: '',
            nombreAlmacen: '',
            direccion: '',
            ciudad: '',
            estadoDir: '',
            telefono: '',
            email: '',
            coordenadasGps: '',
            capacidadM2: null,
            observaciones: '',
            activo: true
        });
        const fetchAlmacenes = async () => {
            isLoading.value = true;
            try {
                // We use a combination of letter and search query for the API
                let finalSearch = searchQuery.value;
                if (selectedLetter.value) {
                    // If there's a letter filter, we could prepend it or handle it specifically
                    // For now, let's assume the API search handles names starting with it if we don't have a specific letter param
                    // But actually, looking at ProveedorRepository pattern, they use a 'letra' param.
                    // Let's check AlmacenRepository if it has 'letra'. It doesn't in my latest edit.
                    // I should probably add 'letra' to AlmacenRepository or just use search.
                    // I'll stick to search for now or add letra if needed.
                }
                const [storesData, typesData] = await Promise.all([
                    AlmacenService.getAllAlmacenes(searchQuery.value, filterTipoId.value || undefined, currentPage.value, itemsPerPage),
                    AlmacenService.getAllTipos()
                ]);
                almacenes.value = storesData.items;
                totalCount.value = storesData.totalCount;
                tiposAlmacen.value = typesData;
            }
            catch (error) {
                console.error(error);
                Swal.fire('Error', 'No se pudieron cargar los almacenes', 'error');
            }
            finally {
                isLoading.value = false;
            }
        };
        onMounted(fetchAlmacenes);
        // Filters logic
        const handleSearch = () => {
            currentPage.value = 1;
            fetchAlmacenes();
        };
        const filterByLetter = (letter) => {
            selectedLetter.value = letter;
            // We'll simulate letter filter with search for now since Repo doesn't have it
            searchQuery.value = letter;
            currentPage.value = 1;
            fetchAlmacenes();
        };
        const resetLetterFilter = () => {
            selectedLetter.value = "";
            searchQuery.value = "";
            currentPage.value = 1;
            fetchAlmacenes();
        };
        const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage) || 1);
        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--;
                fetchAlmacenes();
            }
        };
        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++;
                fetchAlmacenes();
            }
        };
        const filteredInventory = computed(() => {
            if (!searchInventoryQuery.value)
                return inventoryItems.value;
            const q = searchInventoryQuery.value.toLowerCase();
            return inventoryItems.value.filter(i => i.nombreProducto.toLowerCase().includes(q) ||
                i.codigoUnico.toLowerCase().includes(q));
        });
        // Form logic
        const openCreateModal = () => {
            isEditing.value = false;
            resetForm();
            isModalOpen.value = true;
            nextTick(() => { setTimeout(initMap, 300); });
        };
        const editAlmacen = (store) => {
            isEditing.value = true;
            form.value = { ...store };
            selectedFile.value = null; // No file selected initially
            isModalOpen.value = true;
            nextTick(() => { setTimeout(initMap, 300); });
        };
        const closeModal = () => {
            isModalOpen.value = false;
        };
        const triggerFileInput = () => {
            fileInput.value?.click();
        };
        const resetForm = () => {
            form.value = {
                almacenId: 0,
                tipoAlmacenId: '',
                numeroExpediente: '',
                numeroTomo: '',
                nombreRegistroMercantil: '',
                numeroRegistro: '',
                numeroAlmacen: '',
                nombreAlmacen: '',
                direccion: '',
                ciudad: '',
                estadoDir: '',
                telefono: '',
                email: '',
                coordenadasGps: '',
                capacidadM2: null,
                observaciones: '',
                activo: true
            };
            selectedFile.value = null;
        };
        const onFileChange = (e) => {
            const file = e.target.files[0];
            if (file && file.type === 'application/pdf') {
                selectedFile.value = file;
            }
            else {
                Swal.fire('Error', 'Solo se permiten archivos PDF', 'warning');
                e.target.value = '';
            }
        };
        const saveAlmacen = async () => {
            isSaving.value = true;
            try {
                const formData = new FormData();
                Object.keys(form.value).forEach(key => {
                    const val = form.value[key];
                    if (val !== null && val !== undefined) {
                        formData.append(key, val.toString());
                    }
                });
                if (selectedFile.value) {
                    formData.append('file', selectedFile.value);
                }
                if (isEditing.value) {
                    await AlmacenService.updateAlmacen(form.value.almacenId, formData);
                    Toast.fire({ icon: 'success', title: 'Almacén actualizado correctamente' });
                }
                else {
                    await AlmacenService.createAlmacen(formData);
                    Toast.fire({ icon: 'success', title: 'Almacén registrado correctamente' });
                }
                closeModal();
                fetchAlmacenes();
            }
            catch (error) {
                Swal.fire('Error', error.message || 'Error al procesar la solicitud', 'error');
            }
            finally {
                isSaving.value = false;
            }
        };
        const toggleActivo = async (store) => {
            const result = await Swal.fire({
                title: '¿Cambiar estado del almacén?',
                text: `¿Estás seguro de que deseas ${store.activo ? 'desactivar' : 'activar'} el almacén "${store.nombreAlmacen}"?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                await AlmacenService.toggleActivo(store.almacenId);
                store.activo = !store.activo;
                Toast.fire({ icon: 'success', title: `Almacén ${store.activo ? 'activado' : 'desactivado'} correctamente` });
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
            }
        };
        const confirmDelete = async (store) => {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: `El almacén "${store.nombreAlmacen}" será eliminado permanentemente.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });
            if (result.isConfirmed) {
                try {
                    await AlmacenService.deleteAlmacen(store.almacenId);
                    Toast.fire({ icon: 'success', title: 'El almacén ha sido eliminado' });
                    fetchAlmacenes();
                    if (almacenes.value.length === 0 && currentPage.value > 1) {
                        currentPage.value--;
                        fetchAlmacenes();
                    }
                }
                catch (error) {
                    Swal.fire('Error', 'No se pudo eliminar el almacén', 'error');
                }
            }
        };
        // --- Inventory logic ---
        const openInventoryModal = async (store) => {
            selectedAlmacen.value = store;
            isInventoryModalOpen.value = true;
            isInventoryLoading.value = true;
            inventoryItems.value = [];
            searchInventoryQuery.value = '';
            try {
                const data = await AlmacenService.getInventario(store.almacenId);
                inventoryItems.value = data;
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo cargar el inventario del almacén', 'error');
                isInventoryModalOpen.value = false;
            }
            finally {
                isInventoryLoading.value = false;
            }
        };
        const closeInventoryModal = () => {
            isInventoryModalOpen.value = false;
            selectedAlmacen.value = null;
        };
        const formatDate = (dateString) => {
            if (!dateString)
                return '—';
            const date = new Date(dateString);
            return date.toLocaleString();
        };
        // --- MAP LOGIC ---
        let map = null;
        let marker = null;
        let geocoder = null;
        const initMap = () => {
            if (typeof window.google === 'undefined')
                return;
            geocoder = new window.google.maps.Geocoder();
            let lat = 10.4806;
            let lng = -66.9036;
            if (form.value.coordenadasGps) {
                const parts = form.value.coordenadasGps.split(',');
                if (parts.length === 2) {
                    lat = parseFloat(parts[0]);
                    lng = parseFloat(parts[1]);
                }
            }
            const mapOptions = {
                center: { lat, lng },
                zoom: 15,
                disableDefaultUI: true,
                zoomControl: true,
            };
            const mapElement = document.getElementById('almacen-map');
            if (!mapElement)
                return;
            map = new window.google.maps.Map(mapElement, mapOptions);
            marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: map,
                draggable: true,
                title: "Ubicación del Almacén",
                animation: window.google.maps.Animation.DROP
            });
            marker.addListener('dragend', () => {
                const pos = marker.getPosition();
                form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
            });
            map.addListener('click', (e) => {
                const pos = e.latLng;
                marker.setPosition(pos);
                form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
            });
        };
        const updateMapFromInput = () => {
            if (!form.value.coordenadasGps || !marker || !map)
                return;
            const parts = form.value.coordenadasGps.split(',');
            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    const pos = { lat, lng };
                    marker.setPosition(pos);
                    map.setCenter(pos);
                }
            }
        };
        let geocodeTimer = null;
        const geocodeAddress = () => {
            if (!geocoder || !map || !marker)
                return;
            clearTimeout(geocodeTimer);
            geocodeTimer = setTimeout(() => {
                const addrParts = [];
                if (form.value.ciudad)
                    addrParts.push(form.value.ciudad);
                if (form.value.estadoDir)
                    addrParts.push(form.value.estadoDir);
                addrParts.push("Venezuela");
                const address = addrParts.join(", ");
                if (addrParts.length <= 1)
                    return;
                geocoder.geocode({ address }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const pos = results[0].geometry.location;
                        map.setCenter(pos);
                        marker.setPosition(pos);
                        form.value.coordenadasGps = `${pos.lat().toFixed(6)}, ${pos.lng().toFixed(6)}`;
                    }
                });
            }, 1000);
        };
        watch(() => form.value.ciudad, geocodeAddress);
        watch(() => form.value.estadoDir, geocodeAddress);
        return {
            P: PERMISSIONS,
            can: hasPermission,
            activeTab, almacenes, tiposAlmacen, isLoading,
            searchQuery, filterTipoId, alphabet, selectedLetter, currentPage, itemsPerPage,
            totalCount, totalPages,
            isModalOpen, isEditing, isSaving, selectedFile,
            isInventoryModalOpen, isInventoryLoading, inventoryItems, selectedAlmacen, searchInventoryQuery,
            form, handleSearch, filterByLetter, resetLetterFilter, prevPage, nextPage,
            openCreateModal, editAlmacen, closeModal, saveAlmacen, toggleActivo, confirmDelete,
            onFileChange, openInventoryModal, closeInventoryModal, filteredInventory, formatDate,
            updateMapFromInput, triggerFileInput, fileInput
        };
    }
};
